'use client';

import { c as _c } from "react/compiler-runtime";
import './card.scss';
import Link from 'next/link';
import z from 'zod';
import { Badge } from '@sola/ark-core/atoms/badge/badge.tsx';
import { Skeleton } from '@sola/ark-core/atoms/skeleton/skeleton.tsx';
import { cn } from '@sola/ark-core/utils/helpers.ts';
import { ArkIcon } from '@sola/ark-icons/lib/ark-icon.tsx';
import { IconNames } from '@sola/schema/lib/icons/icon-names.ts';
import { BadgeColors } from '@sola/schema/lib/schema.ts';
import { getElapsed } from '@sola/utils/relativeTime.ts';
import { Image, ImageSizeIntent } from '../../atoms/image/image.tsx';
import { Blockquote } from '../blockquote/blockquote.tsx';
import { FeatureBase, FeatureBaseProps } from '../feature-base/feature-base.tsx';
export const CardProps = FeatureBaseProps.extend({
  variant: z.enum(['standard', 'background', 'floating', 'icon', 'stat', 'post', 'article', 'listItem', 'category']).optional(),
  badge: z.object({
    text: z.string().optional(),
    color: BadgeColors.optional(),
    size: z.enum(['md', 'lg']).optional(),
    iconBefore: IconNames.optional()
  }).optional(),
  hasPlaceholder: z.boolean().optional(),
  onClick: z.function().optional(),
  imageIntent: ImageSizeIntent.optional()
});
export type CardProps = z.infer<typeof CardProps>;
const DateAndElapsed = t0 => {
  const $ = _c(9);
  const {
    dateTime,
    locale
  } = t0;
  let elapsed;
  let t1;
  let t2;
  if ($[0] !== dateTime || $[1] !== locale) {
    const ms = Date.parse(dateTime.toString());
    elapsed = getElapsed(dateTime, locale);
    t1 = "Card__date label-potent label-lg";
    t2 = elapsed && <><span className="Card__date--elapsed" suppressHydrationWarning={true}>{elapsed}</span><span suppressHydrationWarning={true}>{new Date(ms).toLocaleString(locale, {
          year: "numeric",
          month: "long",
          day: "numeric"
        })}</span></>;
    $[0] = dateTime;
    $[1] = locale;
    $[2] = elapsed;
    $[3] = t1;
    $[4] = t2;
  } else {
    elapsed = $[2];
    t1 = $[3];
    t2 = $[4];
  }
  const t3 = !elapsed && "Invalid Date";
  let t4;
  if ($[5] !== t1 || $[6] !== t2 || $[7] !== t3) {
    t4 = <p className={t1}>{t2}{t3}</p>;
    $[5] = t1;
    $[6] = t2;
    $[7] = t3;
    $[8] = t4;
  } else {
    t4 = $[8];
  }
  return t4;
};
export const Card: React.FC<CardProps> = t0 => {
  const $ = _c(51);
  let badge;
  let className;
  let displayDate;
  let icon;
  let imageIntent;
  let media;
  let props;
  let t1;
  let t2;
  let t3;
  if ($[0] !== t0) {
    ({
      seoLevel: t1,
      media,
      icon,
      badge,
      variant: t2,
      className,
      hasPlaceholder: t3,
      displayDate,
      imageIntent,
      ...props
    } = t0);
    $[0] = t0;
    $[1] = badge;
    $[2] = className;
    $[3] = displayDate;
    $[4] = icon;
    $[5] = imageIntent;
    $[6] = media;
    $[7] = props;
    $[8] = t1;
    $[9] = t2;
    $[10] = t3;
  } else {
    badge = $[1];
    className = $[2];
    displayDate = $[3];
    icon = $[4];
    imageIntent = $[5];
    media = $[6];
    props = $[7];
    t1 = $[8];
    t2 = $[9];
    t3 = $[10];
  }
  const seoLevel = t1 === undefined ? 3 : t1;
  const variant = t2 === undefined ? "standard" : t2;
  const hasPlaceholder = t3 === undefined ? false : t3;
  const hasButtons = !!props.primaryCTA || !!props.secondaryCTA || !!props.tertiaryCTA;
  const hasContent = !!props.title || !!props.subtitle || !!props.body || !!props.quotation || hasButtons;
  const isPost = variant === "post";
  const isArticle = variant === "article";
  const isCategory = variant === "category";
  const isClickable = props.primaryCTA?.link && (isPost || isCategory);
  let Comp = "div" as React.ElementType;
  let t4;
  if ($[11] === Symbol.for("react.memo_cache_sentinel")) {
    t4 = {};
    $[11] = t4;
  } else {
    t4 = $[11];
  }
  let CompProps = t4;
  if (isClickable && props.primaryCTA?.link) {
    Comp = Link;
    let t5;
    if ($[12] !== props.onClick || $[13] !== props.primaryCTA.link || $[14] !== props.primaryCTA.target) {
      t5 = {
        href: props.primaryCTA.link,
        target: props.primaryCTA.target,
        onClick: props.onClick
      };
      $[12] = props.onClick;
      $[13] = props.primaryCTA.link;
      $[14] = props.primaryCTA.target;
      $[15] = t5;
    } else {
      t5 = $[15];
    }
    CompProps = t5;
  }
  const hasBaseIcon = variant !== "stat" && variant !== "article" && variant !== "category" && (variant === "icon" || !badge?.text);
  const T0 = Comp;
  const t5 = CompProps;
  const t6 = `Card Card--${variant}`;
  const t7 = isCategory ? "w-full" : "w-full max-w-xl mx-auto";
  const t8 = !hasContent;
  let t9;
  if ($[16] !== className || $[17] !== t6 || $[18] !== t7 || $[19] !== t8) {
    t9 = cn(t6, t7, className, {
      "no-content": t8
    });
    $[16] = className;
    $[17] = t6;
    $[18] = t7;
    $[19] = t8;
    $[20] = t9;
  } else {
    t9 = $[20];
  }
  let t10;
  if ($[21] !== badge || $[22] !== icon || $[23] !== isPost || $[24] !== variant) {
    t10 = badge?.text && !isPost && <Badge label={badge.text} size={badge.size} color={badge.color} iconBefore={badge.iconBefore || icon && variant !== "icon" ? icon : undefined} />;
    $[21] = badge;
    $[22] = icon;
    $[23] = isPost;
    $[24] = variant;
    $[25] = t10;
  } else {
    t10 = $[25];
  }
  let t11;
  if ($[26] !== hasContent || $[27] !== hasPlaceholder || $[28] !== imageIntent || $[29] !== media?.image) {
    t11 = (media?.image || hasPlaceholder) && <div className="Card__media"><Image image={media?.image} className={cn("Card__image")} hasPlaceholder={true} sizeIntent={imageIntent} />{hasContent && <div className="Card__media-overlay" />}</div>;
    $[26] = hasContent;
    $[27] = hasPlaceholder;
    $[28] = imageIntent;
    $[29] = media?.image;
    $[30] = t11;
  } else {
    t11 = $[30];
  }
  let t12;
  if ($[31] !== badge || $[32] !== displayDate || $[33] !== hasBaseIcon || $[34] !== hasContent || $[35] !== icon || $[36] !== isArticle || $[37] !== isCategory || $[38] !== isClickable || $[39] !== isPost || $[40] !== props || $[41] !== seoLevel || $[42] !== variant) {
    t12 = hasContent && <div className="Card__content">{isArticle && !!displayDate && <DateAndElapsed dateTime={displayDate} />}<FeatureBase seoLevel={seoLevel} {...props} {...hasBaseIcon && {
        icon
      }} {...variant === "listItem" && {
        variant: "listItem"
      }} {...variant === "stat" && {
        variant: "stat",
        titleSize: "xl"
      }} {...variant === "post" && {
        isClickable: true
      }} {...variant === "article" && {
        primaryCTA: undefined,
        secondaryCTA: undefined,
        tertiaryCTA: undefined
      }} {...variant === "category" && {
        subtitle: undefined,
        body: props.subtitle || props.body,
        primaryCTA: undefined,
        secondaryCTA: undefined,
        tertiaryCTA: undefined,
        titleSize: "sm"
      }} {...variant === "background" && props.quotation?.text && {
        body: <>{props.body}<div className={cn("Card__quotation")}><Blockquote quotation={props.quotation} size="lg" /></div></>,
        quotation: undefined
      }} className={cn({
        "max-w-md": !isCategory,
        "has-checkmarks": variant !== "background" && !isCategory
      }, isCategory && "w-full")} variantSpacing="card" alignment="left" inline={true} handleTitleClick={props.onClick} />{isCategory && <span className="Card__category-icon-slot md:hidden" aria-hidden="true">{isClickable && <ArkIcon iconName="chevron-right" size="1.5rem" className="Card__category-icon" />}</span>}{badge?.text && isPost && <Badge label={badge.text} size="lg" color={badge.color} iconBefore={badge.iconBefore || icon ? icon : undefined} />}</div>;
    $[31] = badge;
    $[32] = displayDate;
    $[33] = hasBaseIcon;
    $[34] = hasContent;
    $[35] = icon;
    $[36] = isArticle;
    $[37] = isCategory;
    $[38] = isClickable;
    $[39] = isPost;
    $[40] = props;
    $[41] = seoLevel;
    $[42] = variant;
    $[43] = t12;
  } else {
    t12 = $[43];
  }
  let t13;
  if ($[44] !== Comp || $[45] !== CompProps || $[46] !== t10 || $[47] !== t11 || $[48] !== t12 || $[49] !== t9) {
    t13 = <T0 {...t5} className={t9}>{t10}{t11}{t12}</T0>;
    $[44] = Comp;
    $[45] = CompProps;
    $[46] = t10;
    $[47] = t11;
    $[48] = t12;
    $[49] = t9;
    $[50] = t13;
  } else {
    t13 = $[50];
  }
  return t13;
};
export const ArticleCardSkeleton: React.FC = () => {
  const $ = _c(4);
  let t0;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t0 = <div className="mb-comfy-sm"><Skeleton width="100%" height="auto" style={{
        aspectRatio: "16/9"
      }} borderRadius="0.25rem" /></div>;
    $[0] = t0;
  } else {
    t0 = $[0];
  }
  let t1;
  if ($[1] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = <div className="mb-comfy-sm"><Skeleton height={20} width="45%" /></div>;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  let t2;
  if ($[2] === Symbol.for("react.memo_cache_sentinel")) {
    t2 = <div className="mb-comfy-sm flex flex-col gap-snug-xs"><Skeleton height={28} width="100%" /><Skeleton height={28} width="85%" /></div>;
    $[2] = t2;
  } else {
    t2 = $[2];
  }
  let t3;
  if ($[3] === Symbol.for("react.memo_cache_sentinel")) {
    t3 = <div className="flex flex-col">{t0}{t1}{t2}<div className="flex flex-col gap-snug-xs"><Skeleton height={20} width="100%" /><Skeleton height={20} width="95%" /></div></div>;
    $[3] = t3;
  } else {
    t3 = $[3];
  }
  return t3;
};